import type { ReactNode } from "react";
import {
  ActivityIndicator,
  ImageBackground,
  Pressable,
  Text,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { haptic, type HapticStyle } from "../haptics";
import { semanticColors, typography } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const velvetCopper =
  require("../assets/images/velvet-copper-btn.jpg") as number;

export type VelvetPrimaryButtonProps = Omit<
  PressableProps,
  "children" | "style"
> & {
  label: string;
  loading?: boolean;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  flex?: number;
  fullWidth?: boolean;
  leading?: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  hapticStyle?: HapticStyle | null;
  disabledOpacity?: number;
  pressedOpacity?: number;
  activityIndicatorColor?: string;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

export function VelvetPrimaryButton({
  label,
  onPress,
  disabled = false,
  loading = false,
  flex,
  fullWidth = true,
  leading,
  icon,
  trailing,
  hapticStyle = null,
  disabledOpacity = 0.4,
  pressedOpacity = 0.82,
  activityIndicatorColor = semanticColors.action.primary.foreground,
  action,
  accessibilityLabel,
  style,
  contentStyle,
  textStyle,
  ...pressableProps
}: VelvetPrimaryButtonProps) {
  const isInactive = disabled || loading;
  const capturePress = usePressCapture("VelvetPrimaryButton");

  return (
    <Pressable
      {...pressableProps}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      accessibilityState={{ disabled: isInactive, busy: loading }}
      disabled={isInactive}
      onPress={(event) => {
        if (isInactive) return;
        capturePress(pressLabel(label, accessibilityLabel), action);
        if (hapticStyle) haptic(hapticStyle);
        onPress?.(event);
      }}
      style={({ pressed }) => [
        {
          ...(flex !== undefined && { flex }),
          alignSelf: fullWidth ? "stretch" : undefined,
          borderRadius: 999,
          overflow: "hidden",
          opacity: disabled ? disabledOpacity : pressed ? pressedOpacity : 1,
        },
        style,
      ]}
    >
      <ImageBackground
        source={velvetCopper}
        resizeMode="cover"
        style={[
          {
            minHeight: 48,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            paddingVertical: 14,
            paddingHorizontal: 24,
            gap: 8,
            // Prevents the background image on web (RN Web renders an <img>)
            // from swallowing clicks so the Pressable above always receives
            // the tap.
            pointerEvents: "none",
          },
          contentStyle,
        ]}
      >
        {loading ? (
          <ActivityIndicator size="small" color={activityIndicatorColor} />
        ) : (
          <>
            {leading ?? icon}
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={[
                {
                  fontSize: 15,
                  lineHeight: 20,
                  fontWeight: "500",
                  fontFamily: typography.families.sans,
                  color: semanticColors.action.primary.foreground,
                  textAlign: "center",
                },
                textStyle,
              ]}
            >
              {label}
            </Text>
            {trailing}
          </>
        )}
      </ImageBackground>
    </Pressable>
  );
}

export default VelvetPrimaryButton;
